import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { cartService } from '../services/cartService';
import { BsArrowLeft, BsCheckCircle, BsTrash } from 'react-icons/bs';
import { fileUrl } from '../constants/endpoints';

const CheckoutPage = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [cart, setCart] = useState(location.state?.cart || null);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_address: '',
    additional_notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Check if current language is Arabic
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    // If no cart data passed through navigation, fetch it
    if (!cart) {
      fetchCart();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCart = async () => {
    try {
      setLoading(true);
      const cartData = await cartService.getCart();
      setCart(cartData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleQuantityChange = async (productId, newQuantity) => {
    if (newQuantity <= 0) {
      await handleRemoveItem(productId);
      return;
    }
    
    try {
      await cartService.updateQuantity(productId, newQuantity);
      const updatedCart = await cartService.getCart();
      setCart(updatedCart);
    } catch (err) {
      console.error('Error updating quantity:', err);
    }
  };

  const handleRemoveItem = async (productId) => {
    try {
      await cartService.removeFromCart(productId);
      const updatedCart = await cartService.getCart();
      setCart(updatedCart);
    } catch (err) {
      console.error('Error removing item:', err);
    }
  };

const validateForm = () => {
  if (!formData.customer_name.trim()) {
    setError(t('Name is required'));
    return false;
  }
  if (!formData.customer_phone.trim()) {
    setError(t('Phone number is required'));
    return false;
  }
  if (formData.customer_phone.trim().length !== 8) {
    setError(t('Phone number must be exactly 8 digits'));
    return false;
  }
  if (!/^\d{8}$/.test(formData.customer_phone)) {
    setError(t('Phone number must contain only numbers'));
    return false;
  }
  if (!formData.customer_address.trim()) {
    setError(t('Address is required'));
    return false;
  }

  return true;
};


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
   
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      await cartService.createOrder(formData);
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToProducts = () => {
    navigate('/products');
  };

  if (loading && !cart) {
    return (
      <div className="pd-top-150" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
        <div className="container">
          <div className="text-center">
            <h3>{t('Loading checkout...')}</h3>
          </div>
        </div>
      </div>
    );
  }

  if (!cart || cart.items.length === 0) {
    return (
      <div className="pd-top-150" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
        <div className="container">
          <div className="text-center" style={{marginBlock:'100px'}}>
            <h3 >{t('Your cart is empty')}</h3>
            <button 
              onClick={handleBackToProducts}
              style={{
                backgroundColor: '#9143DA',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                marginTop: '20px',
                cursor: 'pointer'
              }}
            >
              {t('Continue Shopping')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="pd-top-150" 
      style={{ 
        paddingTop: window.innerWidth <= 768 ? '220px' : '280px', 
        paddingBottom: '60px',
        minHeight: '100vh', 
        backgroundColor: '#f8f9fa',
        direction: isRTL ? 'rtl' : 'ltr'
      }}
    >
      <div className="container">
        {success ? (
          // Success Screen
          <div className="row justify-content-center">
            <div className={window.innerWidth <= 768 ? 'col-12' : 'col-lg-6'}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '15px',
                padding: window.innerWidth <= 768 ? '30px 20px' : '40px',
                textAlign: 'center',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                direction: isRTL ? 'rtl' : 'ltr'
              }}>
                <BsCheckCircle size={window.innerWidth <= 768 ? 60 : 80} color="#28a745" style={{ marginBottom: '20px' }} />
                <h2 style={{ 
                  color: '#28a745', 
                  marginBottom: '15px',
                  fontSize: window.innerWidth <= 768 ? '20px' : '24px'
                }}>{t('Order Placed Successfully!')}</h2>
                <p style={{ 
                  color: '#666', 
                  fontSize: window.innerWidth <= 768 ? '12px' : '14px', 
                  marginBottom: '30px' 
                }}>
                  {t('We will contact you soon to confirm your order and arrange delivery.')}
                </p>
                <button
                  onClick={handleBackToProducts}
                  style={{
                    padding: window.innerWidth <= 768 ? '10px 20px' : '12px 30px',
                    border: 'none',
                    borderRadius: '8px',
                    backgroundColor: '#9143DA',
                    color: 'white',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: window.innerWidth <= 768 ? '14px' : '16px',
                    width: window.innerWidth <= 768 ? '100%' : 'auto'
                  }}
                >
                  {t('Continue Shopping')}
                </button>
              </div>
            </div>
          </div>
        ) : (
          // Checkout Form - Matching the design
          <>
            {/* Header */}
            <div className="row mb-4">
              <div className="col-12">
                <button 
                  onClick={() => navigate(-1)}
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid #9143DA',
                    color: '#9143DA',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    flexDirection: isRTL ? 'row-reverse' : 'row'
                  }}
                >
                  <BsArrowLeft style={{ transform: isRTL ? 'rotate(180deg)' : 'none' }} /> 
                  {t('Back')}
                </button>
              </div>
            </div>

            {/* First Row - Panier Card - Fix RTL spacing */}
            <div className="row mb-4">
              <div className="col-12">
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '15px',
                  padding: '25px 30px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '20px'
                }}>
                  {/* Left Side - Panier Title and Description */}
                  <div style={{ 
                    textAlign: isRTL ? 'right' : 'left',
                    flex: '1'
                  }}>
                    <h2 style={{
                      fontSize: '24px',
                      fontWeight: 'bold',
                      color: '#2c3e50',
                      marginBottom: '8px',
                      margin: '0 0 8px 0'
                    }}>
                      {t('Panier')}
                    </h2>
                    <p style={{
                      color: '#666',
                      marginBottom: '0',
                      fontSize: '14px',
                      margin: '0'
                    }}>
                      {t('Vous avez ajouter au panier')} {cart.items.length} {t('article')}
                    </p>
                  </div>

                  {/* Right Side - Item Count and Continue Shopping */}
                  <div style={{ 
                    textAlign: isRTL ? 'left' : 'right',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: isRTL ? 'flex-start' : 'flex-end',
                    gap: '8px',
                    flexShrink: 0
                  }}>
                    <span style={{
                      color: '#9143DA',
                      fontSize: '18px',
                      fontWeight: 'bold'
                    }}>
                      {cart.items.length} {t('articles')}
                    </span>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      flexDirection: isRTL ? 'row-reverse' : 'row'
                    }}>
                      <span style={{ 
                        color: '#666', 
                        fontSize: '14px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                      onClick={() => navigate('/products')}
                      >
                        {t('continuer mes achats')}
                      </span>
                      <button
                        onClick={() => navigate('/products')}
                        style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          backgroundColor: 'transparent',
                          border: '1px solid #9143DA',
                          color: '#9143DA',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '16px',
                          fontWeight: 'bold',
                          flexShrink: 0
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Second Row - Cart Items - Force LTR for product cards */}
            <div className="row mb-4">
              <div className="col-12">
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '15px',
                  padding: '30px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                }}>
                  {cart.items.map((item) => (
                    <div key={item.id} style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      padding: '20px 0',
                      borderBottom: cart.items.indexOf(item) !== cart.items.length - 1 ? '1px solid #eee' : 'none',
                      direction: 'ltr', // Force LTR for product cards
                      flexDirection: window.innerWidth <= 768 ? 'column' : 'row' // Stack vertically on mobile
                    }}>
                      {/* Mobile Layout */}
                      {window.innerWidth <= 768 ? (
                        <>
                          {/* Product Image and Info Row - Mobile */}
                          <div style={{ 
                            display: 'flex',
                            width: '100%',
                            marginBottom: '15px'
                          }}>
                            {/* Product Image */}
                            <div style={{ 
                              width: '100px', 
                              height: '100px', 
                              marginRight: '15px',
                              flexShrink: 0
                            }}>
                              {item.image_id ? (
                                <img
                                  src={`${fileUrl}/${item.image_id}`}
                                  alt={item.product_name}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                    borderRadius: '8px',
                                  }}
                                />
                              ) : (
                                <div style={{
                                  width: '100%',
                                  height: '100%',
                                  backgroundColor: '#f8f9fa',
                                  borderRadius: '8px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: '#666',
                                  fontSize: '10px',
                                }}>
                                  {t('No Image')}
                                </div>
                              )}
                            </div>
                            
                            {/* Product Info */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <h4 style={{ 
                                margin: '0 0 8px 0', 
                                fontSize: '14px',
                                fontWeight: 'bold',
                                color: '#2c3e50'
                              }}>{item.product_name}</h4>
                              
                              {/* Academic Info */}
                              {(item.institut_grade || item.institut_speciality) && (
                                <p style={{ 
                                  margin: '0 0 8px 0', 
                                  color: '#666', 
                                  fontSize: '12px' 
                                }}>
                                  {item.institut_grade}
                                  {item.institut_grade && item.institut_speciality ? ' ' : ''}
                                  {item.institut_speciality}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Controls Row - Mobile */}
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                            gap: '15px'
                          }}>
                            {/* Quantity Controls */}
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              border: '1px solid #ddd',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              width: 'fit-content'
                            }}>
                              <button
                                onClick={() => handleQuantityChange(item.product_id, item.quantity - 1)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  padding: '8px 12px',
                                  cursor: 'pointer',
                                  color: '#666'
                                }}
                              >
                                -
                              </button>
                              <span style={{
                                padding: '8px 16px',
                                borderLeft: '1px solid #ddd',
                                borderRight: '1px solid #ddd',
                                fontSize: '14px',
                                minWidth: '40px',
                                textAlign: 'center'
                              }}>
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => handleQuantityChange(item.product_id, item.quantity + 1)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  padding: '8px 12px',
                                  cursor: 'pointer',
                                  color: '#666'
                                }}
                              >
                                +
                              </button>
                            </div>

                            {/* Price and Delete - Mobile */}
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '15px'
                            }}>
                              <div style={{
                                fontSize: '16px',
                                fontWeight: 'bold',
                                color: '#9143DA'
                              }}>
                                {item.subtotal.toFixed(2)} {t('TND')}
                              </div>
                              
                              <button
                                onClick={() => handleRemoveItem(item.product_id)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#dc3545',
                                  cursor: 'pointer',
                                  padding: '8px'
                                }}
                              >
                                <BsTrash size={16} />
                              </button>
                            </div>
                          </div>
                        </>
                      ) : (
                        /* Desktop Layout */
                        <>
                          {/* Product Image - Always on left */}
                          <div style={{ 
                            width: '160px', 
                            height: '160px', 
                            marginRight: '20px',
                            flexShrink: 0
                          }}>
                            {item.image_id ? (
                              <img
                                src={`${fileUrl}/${item.image_id}`}
                                alt={item.product_name}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'contain',
                                  borderRadius: '8px',
                                }}
                              />
                            ) : (
                              <div style={{
                                width: '100%',
                                height: '100%',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#666',
                                fontSize: '10px',
                              }}>
                                {t('No Image')}
                              </div>
                            )}
                          </div>
                          
                          {/* Product Info - Always in middle */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <h4 style={{ 
                              margin: '0 0 8px 0', 
                              fontSize: '16px',
                              fontWeight: 'bold',
                              color: '#2c3e50'
                            }}>{item.product_name}</h4>
                            
                            {/* Academic Info */}
                            {(item.institut_grade || item.institut_speciality) && (
                              <p style={{ 
                                margin: '0 0 15px 0', 
                                color: '#666', 
                                fontSize: '14px' 
                              }}>
                                {item.institut_grade}
                                {item.institut_grade && item.institut_speciality ? ' ' : ''}
                                {item.institut_speciality}
                              </p>
                            )}
                            
                            {/* Quantity Controls */}
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              border: '1px solid #ddd',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              width: 'fit-content'
                            }}>
                              <button
                                onClick={() => handleQuantityChange(item.product_id, item.quantity - 1)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  padding: '8px 12px',
                                  cursor: 'pointer',
                                  color: '#666'
                                }}
                              >
                                -
                              </button>
                              <span style={{
                                padding: '8px 16px',
                                borderLeft: '1px solid #ddd',
                                borderRight: '1px solid #ddd',
                                fontSize: '14px',
                                minWidth: '40px',
                                textAlign: 'center'
                              }}>
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => handleQuantityChange(item.product_id, item.quantity + 1)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  padding: '8px 12px',
                                  cursor: 'pointer',
                                  color: '#666'
                                }}
                              >
                                +
                              </button>
                            </div>
                          </div>

                          {/* Price and Delete - Always on right */}
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            gap: '15px',
                            marginLeft: '20px'
                          }}>
                            <div style={{
                              fontSize: '18px',
                              fontWeight: 'bold',
                              color: '#9143DA'
                            }}>
                              {item.subtotal.toFixed(2)} {t('TND')}
                            </div>
                            
                            <button
                              onClick={() => handleRemoveItem(item.product_id)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#dc3545',
                                cursor: 'pointer',
                                padding: '8px'
                              }}
                            >
                              <BsTrash size={16} />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Third Row - Delivery Fee Card */}
            <div className="row mb-4">
              <div className="col-12">
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '15px',
                  padding: '25px 30px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ fontSize: '16px', color: '#666' }}>{t('Frais de Livraison')}</span>
                  <span style={{ 
                    fontSize: '18px', 
                    fontWeight: 'bold', 
                    color: '#2c3e50' 
                  }}>{cart.shipping_fee.toFixed(2)} {t('TND')}</span>
                </div>
              </div>
            </div>

            {/* Fourth Row - Total Card */}
            <div className="row mb-4">
              <div className="col-12">
                <div style={{
                  backgroundColor: '#9143DA',
                  borderRadius: '15px',
                  padding: '25px 30px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  color: 'white'
                }}>
                  <span style={{ fontSize: '18px', fontWeight: 'bold' }}>{t('Total à Payer')}</span>
                  <span style={{ 
                    fontSize: '24px', 
                    fontWeight: 'bold'
                  }}>{cart.total.toFixed(2)} {t('TND')}</span>
                </div>
              </div>
            </div>

            {/* Fifth Row - Order Form */}
            <div className="row">
              <div className="col-12">
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '15px',
                  padding: '30px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  height: 'fit-content'
                }}>
                  <h3 style={{ 
                    marginBottom: '25px', 
                    color: '#2c3e50',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    textAlign: 'center'
                  }}>{t("S'il vous plaît, Remplir le Formulaire pour Confirmer Votre Commande")}</h3>
                  
                  <form onSubmit={handleSubmit}>
                    <div style={{ marginBottom: '20px' }}>
                      <input
                        type="text"
                        name="customer_name"
                        value={formData.customer_name}
                        onChange={handleInputChange}
                        required
                        style={{
                          width: '100%',
                          padding: '15px',
                          border: '1px solid #ddd',
                          borderRadius: '25px',
                          fontSize: '16px',
                          boxSizing: 'border-box',
                          textAlign: 'center'
                        }}
                        placeholder={t('Nom & Prénom*')}
                      />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                      <input
                        type="text"
                        name="customer_phone"
                        value={formData.customer_phone}
                        onChange={handleInputChange}
                        required
                        style={{
                          width: '100%',
                          padding: '15px',
                          border: '1px solid #ddd',
                          borderRadius: '25px',
                          fontSize: '16px',
                          boxSizing: 'border-box',
                          textAlign: 'center'
                        }}
                        placeholder={t('Numéro de téléphone*')}
                        maxLength={8}
                      />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                      <textarea
                        name="customer_address"
                        value={formData.customer_address}
                        onChange={handleInputChange}
                        required
                        rows={4}
                        style={{
                          width: '100%',
                          padding: '15px',
                          border: '1px solid #ddd',
                          borderRadius: '25px',
                          fontSize: '16px',
                          resize: 'vertical',
                          boxSizing: 'border-box',
                          textAlign: 'center'
                        }}
                        placeholder={t('Adresse Exacte*')}
                      />
                    </div>

                    <div style={{ marginBottom: '25px' }}>
                      <textarea
                        name="additional_notes"
                        value={formData.additional_notes}
                        onChange={handleInputChange}
                        rows={3}
                        style={{
                          width: '100%',
                          padding: '15px',
                          border: '1px solid #ddd',
                          borderRadius: '25px',
                          fontSize: '16px',
                          resize: 'vertical',
                          boxSizing: 'border-box',
                          textAlign: 'center'
                        }}
                        placeholder={t('Notes supplémentaires (optionnel)')}
                      />
                    </div>

                    {error && (
                      <div style={{ 
                        backgroundColor: '#f8d7da',
                        color: '#721c24',
                        padding: '12px',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        border: '1px solid #f5c6cb',
                        fontSize: '14px'
                      }}>
                        {error}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={loading}
                      style={{
                        width: '100%',
                        padding: '15px',
                        border: 'none',
                        borderRadius: '25px',
                        backgroundColor: loading ? '#ccc' : '#9143DA',
                        color: 'white',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        fontWeight: 'bold',
                        fontSize: '16px',
                        transition: 'background-color 0.3s ease'
                      }}
                    >
                      {loading ? t('Placing Order...') : t('Achetez')}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CheckoutPage;
